/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable no-console */
import { ProductName } from '@pypestream/api-services';
import { allLocales, TranslationData } from '@pypestream/translations';
import { css, html, nothing, unsafeCSS } from 'lit';
import { property } from 'lit/decorators.js';

import {
  BaseElement,
  customElement,
  SlotObserver,
  watch,
} from '../../components/base-element';
import { Translate } from '../../components/base-element/mixins/translation-mixin';
import {
  getSmartServiceUIState,
  getStateChanges,
  handleGoHome,
} from '../utils';
import { smartService } from '../xstate/smart.xstate';
import { SmartContext } from '../xstate/smart.xstate-utils';

import '../../components/buttons/icon-button/icon-button.wc';
import '../../components/menu/src/menu-item.wc';
import '../../components/nav/nav-link.wc';
import '../../components/nav/nav-links.wc';
import '../../components/nav/nav-logo.wc';
import '../../components/nav/nav.wc';
import '../../components/text/text.wc';
import '../nav-breadcrumb/nav-breadcrumb.wc';
import '../universal-user-dropdown/universal-user-dropdown-trigger.wc';
import '../universal-user-dropdown/universal-user-dropdown.wc';

import megaMenuLoaderStyles from '../mega-menu-loader.scss?inline';

const navTranslations = allLocales.filter(
  (locale) => locale.namespace === 'navigation'
) as unknown as TranslationData[];

/**
 * ### Component Description ###
 * `ps-universal-nav` is a component that provides a navigation bar with support for:
 *  - *nav-breadcrumb*
 *  - *navigation links*
 *  - *additional actions*
 *  - *profile dropdown*
 *
 *
 * ### Features ###
 * The component fetches necessary data (user information, projects, products and their roles) to display information in ***Mega Menu***
 *
 * The component fetches user information and displays the *user's name*, *email*, and *profile photo* in the ***Profile Dropdown***
 */
function isConfigured(): boolean {
  return (
    smartService.initialized &&
    !smartService.state.matches('unconfigured') &&
    !smartService.state.matches('validating') &&
    !smartService.state.matches('incompatible')
  );
}

/**
 * Resolves once the smart service finishes its startup probe and reaches the
 * `configured` state. Rejects if validation fails (the machine transitions to
 * `incompatible`).
 *
 * The state machine only handles `changeApp` / `changeOrg` from `configured`;
 * events sent earlier are silently dropped. Callers that need to dispatch
 * those events must wait for this promise before doing so.
 */
function waitForConfigured(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (isConfigured()) {
      resolve();
      return;
    }
    const sub = smartService.subscribe((state) => {
      if (isConfigured()) {
        sub.unsubscribe();
        resolve();
      } else if (state.matches('incompatible')) {
        sub.unsubscribe();
        reject(new Error('Smart service validation failed'));
      }
    });
  });
}

@customElement('ps-universal-nav')
export class UniversalNavWC extends Translate(
  SlotObserver(BaseElement, ['[slot="nav-breadcrumb-extra"]'])
) {
  static styles = [
    unsafeCSS(megaMenuLoaderStyles),
    css`
      :host {
        display: contents;
      }
    `,
  ];

  context: SmartContext;

  @property({ type: Array }) translations: TranslationData[] | undefined =
    navTranslations;

  @watch('translations')
  onTranslationsChanged() {
    if (this.translations?.length) {
      // use method from translation mixin to add external translations to i18next
      // @ts-ignore
      this.loadTranslations(this.translations);
    }
  }

  /**
   * current ***App name*** (ProductName.Organization | ProductName.AgentAssist | ProductName.Analytics)
   */
  @property({ reflect: true }) app: ProductName = ProductName.Organization;

  @property({ reflect: false, type: Boolean }) isDisabledOrgs?: boolean = false;

  /**
   * current ***Organization id***
   */
  @property({ reflect: false }) orgId: string;

  /**
   * current project
   */
  @property({ reflect: false }) projectId: string;

  /**
   * LaunchDarkly flags, supplied by the host app.
   *
   * The nav cannot read them itself: `SmartServiceConfig.launchDarklyKey` is
   * accepted but unused, and nothing dispatches `featureFlags.initialized`, so
   * the smart machine's own `featureFlags` is always undefined.
   */
  @property({ attribute: false }) featureFlags?: Record<string, boolean>;

  /**
   * optional setting to hide the "go back home" link that normally shows up
   */
  @property({ reflect: false, type: Boolean }) hideGoHome?: boolean = false;

  @property({ type: Boolean, reflect: false }) shouldUpdateUser?: boolean =
    false;

  @property() onManagerToolClick?: (route: string) => void;

  // Private properties for debouncing and error handling
  private _initTimeout?: ReturnType<typeof setTimeout>;

  private _isInitializing = false;

  private _subscription?: { unsubscribe: () => void };

  private _hasPendingUserUpdate = false;

  @watch(['shouldUpdateUser', 'onManagerToolClick', 'orgId', 'app'])
  onPropsChanged() {
    if (this.shouldUpdateUser) {
      this._hasPendingUserUpdate = true;
    }

    // Debounce to prevent rapid successive calls
    clearTimeout(this._initTimeout);
    this._initTimeout = setTimeout(() => this.init(), 100);
  }

  private onProjectSelect(selection: string | undefined) {
    this.emit('project-select', { detail: { selection } });
  }

  /**
   * a method that fires when the user switches the current organization
   */
  private onSelectionChange(selection: string | undefined) {
    this.emit('selection-change', { detail: { selection } });
  }

  constructor() {
    super();
    this.init = this.init.bind(this);
  }

  async connectedCallback() {
    // eslint-disable-next-line wc/guard-super-call
    super.connectedCallback();

    try {
      await this.init();
    } catch (error) {
      console.error('UniversalNav initialization failed:', error);
      // @todo - Handle errors gracefully
    }

    // Set up subscription with error handling
    try {
      this._subscription = smartService.subscribe((smartState) => {
        try {
          const diff = getStateChanges<SmartContext>(
            smartState.context,
            this.context
          );

          if (diff) {
            this.context = smartState.context;
          }

          if (
            this.app !== ProductName.Organization &&
            smartState.context.hasAvailableTools === false
          ) {
            try {
              smartService.send({
                type: 'resetHasAvailableTools',
              });
              handleGoHome({ accountId: smartState.context.accountId });
            } catch (error) {
              console.error('Failed to handle navigation:', error);
              // @todo - Handle errors gracefully
            }
          }
        } catch (error) {
          console.error('Error in smartService subscription:', error);
        }
      });
    } catch (error) {
      console.error('Failed to set up smartService subscription:', error);
    }
  }

  disconnectedCallback() {
    // Clean up subscription
    if (this._subscription) {
      this._subscription.unsubscribe();
      this._subscription = undefined;
    }

    // Clear any pending timeouts
    if (this._initTimeout) {
      clearTimeout(this._initTimeout);
      this._initTimeout = undefined;
    }

    super.disconnectedCallback();
  }

  async init() {
    if (this._isInitializing) {
      return;
    }
    this._isInitializing = true;

    try {
      if (!isConfigured()) {
        await waitForConfigured();
      }

      if (this.app !== this.context?.app) {
        smartService.send({
          type: 'changeApp',
          app: this.app,
        });
      }

      if (this.context?.accountId !== this.orgId) {
        smartService.send({
          type: 'changeOrg',
          orgId: this.orgId,
        });
      }

      if (this._hasPendingUserUpdate) {
        smartService.send('loadUser');
        this._hasPendingUserUpdate = false;
      }
    } catch (error) {
      console.error('Error in init state machine operations:', error);
    } finally {
      this._isInitializing = false;
    }
  }

  render() {
    const { isLoading, isError } = getSmartServiceUIState();

    if (isLoading) {
      return html`<ps-nav><div class="c-mega-menu__loader"></div></ps-nav>`;
    }

    if (isError) {
      return html`
        <ps-nav>
          <ps-nav-logo></ps-nav-logo>
          <ps-text variant="danger"
            >Something went wrong. Please try again later.</ps-text
          >
        </ps-nav>
      `;
    }

    return html`
      <ps-nav>
        <ps-nav-breadcrumb
          .app=${this.app}
          .projectId=${this.projectId}
          .hideGoHome=${this.hideGoHome}
          .onProjectSelect=${this.onProjectSelect}
          .onSelectionChange=${this.onSelectionChange}
          .onManagerToolClick=${this.onManagerToolClick}
          .featureFlags=${this.featureFlags}
          ?isDisabledOrgs=${this.isDisabledOrgs}
        >
          ${this.hasSlottedContent('[slot="nav-breadcrumb-extra"]')
            ? html`<ps-icon
                  name="slash"
                  size="xsmall"
                  color="gray"
                  slot="nav-breadcrumb-extra"
                ></ps-icon>
                <slot name="nav-breadcrumb-extra" slot="nav-breadcrumb-extra">
                </slot>`
            : nothing}
        </ps-nav-breadcrumb>
        <slot name="navigation"></slot>
        <slot name="extra"></slot>
        <slot name="controls"></slot>

        <ps-nav-actions>
          <slot name="actions"></slot>
        </ps-nav-actions>

        <slot style="display: block; grid-area: profile;"></slot>
      </ps-nav>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ps-universal-nav': UniversalNavWC;
  }
  enum PSElementTagNameMap {
    'ps-universal-nav' = 'ps-universal-nav',
  }
}
